import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule, Optional, SkipSelf } from '@angular/core';
import * as i1 from '@angular/router';
import { from } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import * as i1$1 from '@angular/common/http';
import { HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
const CRUD_OPTIONS = new InjectionToken('CRUD_OPTIONS');
class ListHelperService {
  constructor(config, router) {
    this.router = router;
    this.defaultPageSize = config.defaultPageSize || 10;
  }
  /**
   * Router-Aufruf zu `endpoint` mit `previousParams` ohne `id`.
   * @param endpoint Aufzurufende URL
   * @param previousParams Aktuelle Filter-Params
   */
  navigateAndPersistParameters(endpoint, previousParams) {
    const {
      id,
      ...withoutId
    } = previousParams;
    this.router.navigate([endpoint, {
      ...withoutId
    }]);
  }
  /**
   * Setze oder überschreibe Filter für `property` in `filter`.
   * Gibt ein neues Filter-Array zurück.
   * @param filter aktuelles Filter-Array
   * @param property zu filterndes Feld
   * @param value Wert
   * @param operator `<`,`>` oder `=`
   * @param overwrite Überschreiben des Filters (default: false)
   */
  addFilter(filter, property, value, operator, overwrite = false) {
    const result = overwrite ? this.removeFilter(filter, property) : [...filter];
    result.push({
      property,
      value,
      operator
    });
    return result;
  }
  /**
   * Listenstatus zum Persistieren in URL vorbereiten.
   * Nur die Objekte persistieren, die geändert wurden.
   * Gibt `FilterOptions` encoded zurück für den Aufruf von
   * `Router.navigate`.
   * @param options Zu enkodierende `FilterOptions`
   */
  encodeStateForUrl(options) {
    const stateObj = {};
    if (options.page && options.page > 0) {
      stateObj.page = options.page;
    }
    if (typeof options.sort === 'string') {
      stateObj.sort = options.sort;
    } else if (options.sort) {
      stateObj.sort = encodeURIComponent(JSON.stringify(options.sort));
    }
    if (options.size && options.size !== this.defaultPageSize) {
      stateObj.size = options.size;
    }
    if (typeof options.filter === 'string') {
      stateObj.filter = options.filter;
    } else if (options.filter && options.filter.length > 0) {
      stateObj.filter = encodeURIComponent(JSON.stringify(options.filter));
    }
    return stateObj;
  }
  /**
   * Entfernt `property` aus `filter`. Gibt die neuen Filter zurück.
   * @param filter aktuelle Filter
   * @param property zu entfernendes Feld
   */
  removeFilter(filter, property) {
    return filter.filter(value => value.property !== property);
  }
  /**
   * Sortierung für ein Feld setzen bzw. togglen zwischen
   * 'asc' und 'desc'. Der Aufruf resultiert in einem `Router.navigate`.
   * @param endpoint Endpoint, der sortiert werden soll
   * @param evt SortEvent, welches ausgewähltes Feld und dessen Richtung enthält
   * @param previousParams Bereits gesetzte params, die persistieren sollen
   */
  toggleSort(endpoint, evt, previousParams) {
    const sortOptions = this.encodeStateForUrl({
      sort: {
        ...evt
      }
    });
    this.router.navigate([endpoint, {
      ...previousParams,
      sort: sortOptions.sort
    }]);
  }
  /**
   * Aktualisiert Pagination-Parameter für `MatPaginator`.
   * Der Aufruf resultiert in einem `Router.navigate`.
   * @param endpoint Endpoint, auf dem paginiert werden soll
   * @param previousParams Bereits gesetzte params, die persistieren sollen
   */
  onPaginateChange(endpoint, evt, previousParams) {
    const newParams = this.encodeStateForUrl({
      ...previousParams,
      size: evt.pageSize ? evt.pageSize : undefined,
      page: evt.pageIndex ? evt.pageIndex : undefined
    });
    this.router.navigate([endpoint, {
      ...newParams
    }]);
  }
  static {
    this.ɵfac = function ListHelperService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ListHelperService)(i0.ɵɵinject(CRUD_OPTIONS), i0.ɵɵinject(i1.Router));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ListHelperService,
      factory: ListHelperService.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ListHelperService, [{
    type: Injectable
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [CRUD_OPTIONS]
    }]
  }, {
    type: i1.Router
  }], null);
})();
class CrudHelperService {
  constructor(config, http) {
    this.http = http;
    this.baseUrlGetter = config.baseUrl;
  }
  /**
   * Laden aller Objekte von `endpoint` unter Berücksichtigung von `filterOptions`
   * (Seite, Seitengröße, Filter und Sortierung).
   * @param endpoint Endpunkt zum Laden aller Objekte
   * @param options Details für die Seite, Seitengröße, Filter und Sortierung.
   * @param formatSort Funktion zum Formatieren der Suche
   */
  getAll(endpoint, filterOptions, formatSort) {
    const options = {
      filter: [],
      ...filterOptions
    };
    options.filter = this.checkFilter(options);
    // Sortierung für API formatieren, wenn vorhanden
    if (options?.sort && Object.keys(options.sort).length !== 0) {
      options.sort = formatSort ? formatSort(options.sort) : this.formatSort(options.sort);
    }
    // Filter als JSON-String
    options.filter = JSON.stringify(options.filter);
    const httpOptions = {
      params: this.serializeSearchParams(options),
      observe: 'body',
      responseType: 'json'
    };
    return from(this.baseUrlGetter()).pipe(mergeMap(baseUrlAsync => this.http.get(baseUrlAsync + endpoint, httpOptions)));
  }
  /**
   * Enkodiere Filter falls dieser dekodiert ist.
   * @param options Details für die Seite, Seitengröße, Filter und Sortierung.
   */
  checkFilter(options) {
    if (!options?.filter || options.filter === '' || Array.isArray(options.filter)) {
      return options.filter;
    }
    return JSON.parse(decodeURIComponent(options.filter));
  }
  /**
   * Speichern (create und update) einer Entität. Ist `entity.id` gesetzt, wird `PUT` verwendet und
   * die URL um `/{id}` ergänzt; ansonsten wird `POST` genutzt.
   * Die Entität wird außerdem von leeren Arrays bereinigt, wenn `clean=true` (default).
   * @param endpoint URL-Endpoint zur Entität
   * @param entity Zu speichernde Entität
   * @param clean Soll die Entität von leeren Arrays bereinigt werden?
   * @param identityProperty Name der ID-Eigenschaft
   */
  save(endpoint, entity, appendUrl, clean = true, identityProperty = 'id') {
    let isUpdate = false;
    let entityUrl = endpoint;
    if (typeof entity !== 'string') {
      isUpdate = entity.hasOwnProperty(identityProperty) && typeof entity[identityProperty] !== 'undefined' && entity[identityProperty] !== '';
      if (isUpdate) {
        entityUrl += `/${entity[identityProperty]}`;
      }
    }
    if (appendUrl) {
      entityUrl += `/${appendUrl}`;
    }
    // clone Entity, damit Änderungen für die API nicht in die View
    // propagiert werden.
    const clonedEntity = JSON.parse(JSON.stringify(entity));
    const cleanedEntity = clean ? this.clean(clonedEntity) : clonedEntity;
    const json = JSON.stringify(cleanedEntity);
    return from(this.baseUrlGetter()).pipe(mergeMap(baseUrlAsync => {
      return isUpdate ? this.http.put(baseUrlAsync + entityUrl, json) : this.http.post(baseUrlAsync + entityUrl, json);
    }));
  }
  /**
   * Laden eines einzelnen Objekts.
   * @param endpoint Endpunkt des zu ladenden Objektes
   */
  get(endpoint) {
    return from(this.baseUrlGetter()).pipe(mergeMap(baseUrlAsync => this.http.get(`${baseUrlAsync}${endpoint}`)));
  }
  /**
   * Löschen eines Objektes.
   * @param endpoint Endpunkt des zu löschenden Objektes.
   */
  delete(endpoint) {
    return from(this.baseUrlGetter()).pipe(mergeMap(baseUrlAsync => this.http.delete(`${baseUrlAsync}${endpoint}`, {
      observe: 'response'
    })));
  }
  /**
   * Speichern einer FormData, wie z. B. Dateien, unter `endpoint`.
   * @param endpoint Endpunkt zum Speichern der FormData
   * @param formData Die zu speichernde FormData
   */
  postFormData(endpoint, formData) {
    let headers = new HttpHeaders();
    headers = headers.set('Accept', '*/*');
    headers = headers.set('X-KDO-NO-CONTENT-TYPE', 'true');
    return from(this.baseUrlGetter()).pipe(mergeMap(baseUrlAsync => this.http.post(baseUrlAsync + endpoint, formData, {
      headers
    })));
  }
  /**
   * Herunterladen einer Datei.
   * @param endpoint Endpunkt zum Herunterladen der Datei
   */
  downloadBlob(endpoint) {
    return from(this.baseUrlGetter()).pipe(mergeMap(baseUrlAsync => this.http.get(baseUrlAsync + endpoint, {
      responseType: 'blob'
    })));
  }
  /**
   * Leere Arrays und Objekte rekursiv entfernen, um mit der
   * API konsistent zu sein.
   * @param  variable Zu säubernde Variable
   * @return           Gesäuberte Variable
   */
  clean(variable) {
    // Nichts zu säubern, wenn kein Array oder Object.
    if (Object.prototype.toString.call(variable) !== '[object Array]' && Object.prototype.toString.call(variable) !== '[object Object]' || Object.prototype.toString.call(variable) === '[object Function]') {
      return variable;
    }
    const clonedVariable = JSON.parse(JSON.stringify(variable));
    // Attribute oder Inhalte des Object/Arrays durchlaufen.
    for (const i of Object.keys(clonedVariable)) {
      if (clonedVariable[i] === null) {
        delete clonedVariable[i];
        continue;
      }
      const length = Object.prototype.toString.call(clonedVariable[i]) === '[object Object]' ? Object.keys(clonedVariable[i]).length : clonedVariable[i].length;
      if (length === 0) {
        // Leere Inhalte löschen.
        delete clonedVariable[i];
      } else {
        // Nicht leere Inhalte wiederum durchlaufen
        clonedVariable[i] = this.clean(clonedVariable[i]);
      }
    }
    return clonedVariable;
  }
  /**
   * Wandelt Key-Value-Paare des Objekts `options` in `HttpParams` um.
   * @param options Key-Value-Paare für die Request-Parameter
   */
  serializeSearchParams(options) {
    let params = new HttpParams();
    Object.keys(options).filter(key => !!options[key]).forEach(key => params = params.set(key, options[key]));
    return params;
  }
  /**
   * Sortierung der API entsprechend umformatieren.
   * @param sort Sortierungsinformationen
   */
  formatSort(sort) {
    const parsedSort = JSON.parse(decodeURIComponent(sort));
    return parsedSort.active + ',' + parsedSort.direction;
  }
  static {
    this.ɵfac = function CrudHelperService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || CrudHelperService)(i0.ɵɵinject(CRUD_OPTIONS), i0.ɵɵinject(i1$1.HttpClient));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: CrudHelperService,
      factory: CrudHelperService.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CrudHelperService, [{
    type: Injectable
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [CRUD_OPTIONS]
    }]
  }, {
    type: i1$1.HttpClient
  }], null);
})();
const DefaultFilename = 'Unbenannt';
const FilenameExtractor = /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/;
/**
 * Helper für Datei-Downloads.
 * Ist kein expliziter Dateiname angegeben und in den GET-Optionen `observe: response` gesetzt,
 * wird der Name automatisch aus dem `Content-Disposition`-Header ausgelesen.
 */
class DownloadHelperService {
  downloadFile(blob, fileName) {
    if (!fileName && blob instanceof HttpResponse) {
      const contDispHeader = blob.headers.get('Content-Disposition');
      if (contDispHeader) {
        const matches = FilenameExtractor.exec(contDispHeader);
        fileName = matches && matches[3] ? matches[3].trim() : DefaultFilename;
      } else {
        fileName = DefaultFilename;
      }
    }
    const fileContent = blob instanceof HttpResponse ? blob.body : blob;
    if (!fileContent) {
      return false;
    }
    const download = URL.createObjectURL(fileContent);
    const a = document.createElement('a');
    a.href = download;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    // Cleanup
    setTimeout(() => {
      URL.revokeObjectURL(download);
      document.getElementsByTagName('body')[0].removeChild(a);
    }, 100);
    return true;
  }
}
class KDOCrudModule {
  static forRoot(options) {
    return {
      ngModule: KDOCrudModule,
      providers: [options.kdoOptionsProvider || {
        provide: CRUD_OPTIONS,
        useValue: options.config
      }, CrudHelperService, ListHelperService, DownloadHelperService]
    };
  }
  constructor(parentModule) {
    if (parentModule) {
      throw new Error(`KDOCrudModule is already loaded.
        It should only be imported in your application's main module.`);
    }
  }
  static {
    this.ɵfac = function KDOCrudModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || KDOCrudModule)(i0.ɵɵinject(KDOCrudModule, 12));
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: KDOCrudModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(KDOCrudModule, [{
    type: NgModule
  }], () => [{
    type: KDOCrudModule,
    decorators: [{
      type: Optional
    }, {
      type: SkipSelf
    }]
  }], null);
})();

/*
 * Public API Surface of ng-crud
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CRUD_OPTIONS, CrudHelperService, DownloadHelperService, KDOCrudModule, ListHelperService };
